import * as localService from "../services/localService";
import { Constants } from "./constants";

/**
 * Route Access Control Utilities
 * Handles role-based access control for application routes
 */

/**
 * Check if current user has access to a specific route
 * @param {string} routePath - The route path to check
 * @returns {boolean} - Whether the current user has access
 */
export const checkCurrentUserRouteAccess = routePath => {
  const user = localService.getUser();
  if (!user) return false;

  const userRole = user.organizationRole?.code || user.role;
  return checkRouteAccess(routePath, userRole);
};

/**
 * Check if a user role has access to a specific route
 * @param {string} routePath - The route path to check
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user has access
 */
export const checkRouteAccess = (routePath, userRole) => {
  // Normalize route path (remove leading slash and handle dynamic segments)
  const normalizedPath = routePath.replace(/^\//, "").split("/")[0];

  // Get all allowed routes for the user's role
  const allowedRoutes = getAllowedRoutesForRole(userRole);

  // Check if the route is in the allowed list
  return allowedRoutes.some(allowedRoute => {
    const normalizedAllowedRoute = allowedRoute.replace(/^\//, "").split("/")[0];
    return normalizedPath === normalizedAllowedRoute;
  });
};

/**
 * Get all routes that a specific role is allowed to access
 * @param {string} role - The user's role
 * @returns {Array<string>} - Array of allowed route paths
 */
export const getAllowedRoutesForRole = role => {
  // Get menus for the role
  const menus = Constants.getMenusByRole(role, true); // Assume active subscription for route checking

  // Extract all route paths from menus (including children)
  const routes = [];

  const extractRoutes = menuItems => {
    menuItems.forEach(menu => {
      if (menu.route) {
        routes.push(menu.route);
      }
      if (menu.children) {
        extractRoutes(menu.children);
      }
    });
  };

  extractRoutes(menus);

  // Add public routes that everyone can access
  const publicRoutes = [
    "/home",
    "/signin",
    "/signup",
    "/confirm",
    "/signup-success",
    "/forgot-password",
    "/reset-password",
    "/terms-of-use",
    "/privacy-policy",
    "/frequently-asked-questions",
    "/contact-us",
    "/about-us",
    "/report-issue",
    "/corporate-page",
    "/authentication/confirm-invite",
    "/select-package",
    "/otp-confirmation",
    "/space/:spaceId",
    "/spaces/:spaceId",
    "/spaces",
    "/workspaces-search",
    "/plan",
    "/plans",
    "/host-experience",
  ];

  // Add host experience routes for host roles
  const hostRoutes = [
    "/host/dashboard",
    "/host/spaces",
    "/host/calendar",
    "/host/clients",
    "/host/analytics",
    "/host/settings",
    "/host/customer-dashboard",
    "/host/feed",
    "/host/my-bookings",
    "/host/messages",
    "/host/book-space",
    "/host/floor-plan",
    "/host/customer-profile",
    "/host/pricing",
    // "/host/customer-help",
  ];

  // Admin-only routes for pricing and membership (only organization admins can access, excluding platform admins)
  const adminOnlyRoutes = ["/membership-plan"];

  const adminRoles = ["ROLE_HOST", "ROLE_SUBSCRIBER_HOST", "ROLE_SUBSCRIBER_OWNER", "ROLE_HOST_CUSTOMER_OWNER"];

  // Add routes based on role type
  if (isHostRole(role)) {
    routes.push(...hostRoutes);
  }

  // Add admin-only routes for admin roles
  if (adminRoles.includes(role)) {
    routes.push(...adminOnlyRoutes);
  }

  // Always add public routes
  routes.push(...publicRoutes);

  return [...new Set(routes)]; // Remove duplicates
};

/**
 * Check if a role is a host-related role
 * @param {string} role - The role to check
 * @returns {boolean} - Whether the role is a host role
 */
export const isHostRole = role => {
  const hostRoles = ["ROLE_HOST", "ROLE_HOST_OPERATIONS", "ROLE_SUBSCRIBER_HOST"];
  return hostRoles.includes(role);
};

/**
 * Check if a route exists in the application
 * @param {string} routePath - The route path to check
 * @returns {boolean} - Whether the route exists
 */
export const isRouteExists = routePath => {
  // List of all valid routes in the application
  const allRoutes = [
    // Public routes
    "/home",
    "/signin",
    "/signup",
    "/confirm",
    "/signup-success",
    "/forgot-password",
    "/reset-password",
    "/terms-of-use",
    "/privacy-policy",
    "/frequently-asked-questions",
    "/contact-us",
    "/about-us",
    "/report-issue",
    "/corporate-page",
    "/authentication/confirm-invite",
    "/select-package",
    "/otp-confirmation",
    "/space/:spaceId",
    "/spaces/:spaceId",
    "/host-experience",

    // Authenticated routes
    "/dashboard",
    "/spaces-and-services",
    "/user-management",
    "/settings",
    "/settings/security",
    "/settings/company/profile",
    "/membership-plan",
    "/billing",
    "/startbooking",
    "/reservations",
    "/booking",
    "/editbooking",
    "/searchPage",
    "/reservation",

    // Admin routes
    "/disabledHosts",
    "/companyApprovals",
    "/approvedHosts",
    "/suspendedHosts",
    "/api-explorer",
    "/roles-permissions",
    "/service-categories",
    "/offers-discounts",
    "/packages",
    "/orders",
    "/announcements",
    "/payment-configuration",
    "/policy-management",
    "/list-property-form",

    // Host experience routes
    "/host",
    "/host/dashboard",
    "/host/spaces",
    "/host/calendar",
    "/host/clients",
    "/host/analytics",
    "/host/settings",
    "/host/pricing",
    "/host/customer-dashboard",
    "/host/feed",
    "/host/my-bookings",
    "/host/messages",
    "/host/book-space",
    "/host/floor-plan",
    "/host/customer-profile",
    // "/host/customer-help",
  ];

  // Check if route exists (including dynamic routes)
  return allRoutes.some(route => {
    // Handle dynamic routes like /space/:spaceId
    if (route.includes(":")) {
      const routePattern = route.replace(/:[^/]+/g, "[^/]+");
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(routePath);
    }
    return route === routePath;
  });
};

/**
 * Get the appropriate fallback route for a user based on their role
 * @param {string} role - The user's role
 * @returns {string} - The fallback route
 */
export const getFallbackRouteForRole = role => {
  if (isHostRole(role)) {
    return "/host/dashboard";
  }

  // Default fallback for other roles
  return "/home";
};

/**
 * Check if a user can access a route and get redirect info if not
 * @param {string} routePath - The route path to check
 * @returns {Object} - { hasAccess: boolean, redirectTo?: string, reason?: string }
 */
export const validateRouteAccess = routePath => {
  const user = localService.getUser();

  // Check if route exists
  if (!isRouteExists(routePath)) {
    return {
      hasAccess: false,
      redirectTo: "/404",
      reason: "Route does not exist",
    };
  }

  // If no user, check if it's a public route
  if (!user) {
    const publicRoutes = [
      "/home",
      "/signin",
      "/signup",
      "/confirm",
      "/signup-success",
      "/forgot-password",
      "/reset-password",
      "/terms-of-use",
      "/privacy-policy",
      "/frequently-asked-questions",
      "/contact-us",
      "/about-us",
      "/report-issue",
      "/corporate-page",
      "/authentication/confirm-invite",
      "/select-package",
      "/otp-confirmation",
      "/space/:spaceId",
      "/spaces/:spaceId",
      "/host-experience",
    ];

    const isPublicRoute = publicRoutes.some(route => {
      if (route.includes(":")) {
        const routePattern = route.replace(/:[^/]+/g, "[^/]+");
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(routePath);
      }
      return route === routePath;
    });

    if (isPublicRoute) {
      return { hasAccess: true };
    } else {
      return {
        hasAccess: false,
        redirectTo: "/signin",
        reason: "Authentication required",
      };
    }
  }

  // Check role-based access
  const userRole = user.organizationRole?.code || user.role;
  const hasAccess = checkRouteAccess(routePath, userRole);

  if (!hasAccess) {
    const fallbackRoute = getFallbackRouteForRole(userRole);
    return {
      hasAccess: false,
      redirectTo: fallbackRoute,
      reason: "Insufficient permissions",
    };
  }

  return { hasAccess: true };
};
